/**
 * NpcBarkOverlay — Player-facing floating toasts for NPC speech
 *
 * Subscribes to chatStore, filters for type === 'npc' messages
 * added in the last 8 seconds. Shows max 2 toasts with auto-dismiss.
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { useChatStore } from '../stores/chatStore';

const AUTO_DISMISS_MS = 6000;
const MAX_TOASTS = 2;
const DEDUP_WINDOW_MS = 10000;

interface BarkToast {
  id: string;
  npcName: string;
  npcTitle?: string;
  factionColor?: string;
  message: string;
  createdAt: number;
}

export function NpcBarkOverlay() {
  const messages = useChatStore((s) => s.messages);
  const [toasts, setToasts] = useState<BarkToast[]>([]);
  const seenRef = useRef<Map<string, number>>(new Map());
  const prevCountRef = useRef(0);

  // Detect new NPC messages
  useEffect(() => {
    if (messages.length <= prevCountRef.current) {
      prevCountRef.current = messages.length;
      return;
    }

    const newMessages = messages.slice(prevCountRef.current);
    prevCountRef.current = messages.length;
    const now = Date.now();

    for (const msg of newMessages) {
      if (msg.type !== 'npc') continue;

      // Dedup: skip if same message text within window
      const lastSeen = seenRef.current.get(msg.message);
      if (lastSeen && now - lastSeen < DEDUP_WINDOW_MS) continue;
      seenRef.current.set(msg.message, now);

      const toast: BarkToast = {
        id: msg.id,
        npcName: msg.npcName || msg.sender,
        npcTitle: msg.npcTitle,
        factionColor: msg.factionColor,
        message: msg.message,
        createdAt: now,
      };

      setToasts((prev) => {
        const next = [...prev, toast];
        // Keep only latest MAX_TOASTS
        if (next.length > MAX_TOASTS) {
          return next.slice(next.length - MAX_TOASTS);
        }
        return next;
      });
    }

    // Clean up old dedup entries
    for (const [key, ts] of seenRef.current.entries()) {
      if (now - ts > DEDUP_WINDOW_MS) seenRef.current.delete(key);
    }
  }, [messages]);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  if (toasts.length === 0) return null;

  return (
    <div style={{
      position: 'fixed',
      bottom: 80,
      left: 20,
      width: 300,
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
      zIndex: 900,
      pointerEvents: 'none',
    }}>
      {toasts.map((toast) => (
        <BarkToastCard key={toast.id} toast={toast} onDismiss={dismiss} />
      ))}
    </div>
  );
}

function BarkToastCard({ toast, onDismiss }: { toast: BarkToast; onDismiss: (id: string) => void }) {
  const [visible, setVisible] = useState(false);
  const [fading, setFading] = useState(false);
  const color = toast.factionColor || '#d4a574';

  // Animate in
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 50);
    return () => clearTimeout(t);
  }, []);

  // Auto-dismiss
  useEffect(() => {
    const fadeTimer = setTimeout(() => setFading(true), AUTO_DISMISS_MS - 500);
    const dismissTimer = setTimeout(() => onDismiss(toast.id), AUTO_DISMISS_MS);
    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(dismissTimer);
    };
  }, [toast.id, onDismiss]);

  return (
    <div
      onClick={() => {
        setFading(true);
        setTimeout(() => onDismiss(toast.id), 300);
      }}
      style={{
        pointerEvents: 'auto',
        cursor: 'pointer',
        padding: '10px 14px',
        backgroundColor: 'rgba(20, 15, 30, 0.92)',
        border: `1px solid ${color}50`,
        borderLeft: `3px solid ${color}`,
        borderRadius: 4,
        boxShadow: `0 4px 16px rgba(0,0,0,0.4), 0 0 8px ${color}15`,
        opacity: fading ? 0 : visible ? 1 : 0,
        transform: `translateY(${visible && !fading ? 0 : 10}px)`,
        transition: 'all 0.4s ease',
      }}
    >
      {/* NPC name + title */}
      <div style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 6,
        marginBottom: 4,
      }}>
        <span style={{
          fontSize: 11,
          fontWeight: 600,
          color,
        }}>
          {toast.npcName}
        </span>
        {toast.npcTitle && (
          <span style={{
            fontSize: 9,
            color: `${color}90`,
            fontStyle: 'italic',
          }}>
            {toast.npcTitle}
          </span>
        )}
      </div>

      {/* Bark text */}
      <div style={{
        fontSize: 12,
        fontFamily: 'Georgia, serif',
        fontStyle: 'italic',
        color: '#f0e8f8',
        lineHeight: 1.5,
      }}>
        &ldquo;{toast.message}&rdquo;
      </div>
    </div>
  );
}
